import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Building2, Plus, Trash2, Edit, MapPin, Phone, Mail, Globe,
  Loader2, CheckCircle, Crown, GitBranch, ChevronDown, ChevronUp, Save, X
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/api';
import authAxios from '../../utils/authAxios';

var REGIONS = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', 'O\'Higgins', 'Maule', 'Ñuble', 'Biobío',
  'Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes'
];

var EMPTY_FORM = {
  name: '', description: '', address: '', city: '', region: '',
  phone: '', whatsapp: '', email: '', website: '',
  modality: [], services: [], specializations: [],
  price_min: '', price_max: '', gender: '',
  logo: '', gallery: []
};

export default function BranchManager({ parentCenter }) {
  var [branches, setBranches] = useState([]);
  var [loading, setLoading] = useState(true);
  var [maxBranches, setMaxBranches] = useState(5);
  var [canAddMore, setCanAddMore] = useState(true);
  var [showForm, setShowForm] = useState(false);
  var [editingBranch, setEditingBranch] = useState(null);
  var [saving, setSaving] = useState(false);
  var [form, setForm] = useState(EMPTY_FORM);
  var [expandedBranch, setExpandedBranch] = useState(null);

  useEffect(function() {
    if (parentCenter?.center_id) {
      loadBranches();
    }
  }, [parentCenter?.center_id]);

  async function loadBranches() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/center/branches/' + parentCenter.center_id, { withCredentials: true });
      setBranches(res.data.branches || []);
      setMaxBranches(res.data.max_branches || 5);
      setCanAddMore(res.data.can_add_more);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  function handleChange(field, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  function openNewForm() {
    setEditingBranch(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEditForm(branch) {
    setEditingBranch(branch.center_id);
    setForm({
      name: branch.name || '',
      description: branch.description || '',
      address: branch.address || '',
      city: branch.city || '',
      region: branch.region || '',
      phone: branch.phone || '',
      whatsapp: branch.whatsapp || '',
      email: branch.email || '',
      website: branch.website || '',
      modality: branch.modality || [],
      services: branch.services || [],
      specializations: branch.specializations || [],
      price_min: branch.price_min || '',
      price_max: branch.price_max || '',
      gender: branch.gender || '',
      logo: branch.logo || '',
      gallery: branch.gallery || []
    });
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.phone || !form.email) {
      toast.error('Completa los campos obligatorios: Nombre, Dirección, Ciudad, Teléfono y Email');
      return;
    }
    setSaving(true);
    try {
      if (editingBranch) {
        await authAxios.put(API + '/center/branches/' + editingBranch, form, { withCredentials: true });
        toast.success('Sucursal actualizada');
      } else {
        var payload = Object.assign({}, form, { parent_center_id: parentCenter.center_id });
        await authAxios.post(API + '/center/branches/add', payload, { withCredentials: true });
        toast.success('Sucursal creada exitosamente');
      }
      setShowForm(false);
      setEditingBranch(null);
      setForm(EMPTY_FORM);
      loadBranches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar sucursal');
    }
    setSaving(false);
  }

  async function handleDelete(branchId, branchName) {
    if (!window.confirm('¿Estás seguro de eliminar la sucursal "' + branchName + '"? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.delete(API + '/center/branches/' + branchId, { withCredentials: true });
      toast.success('Sucursal eliminada');
      loadBranches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  }

  function toggleModality(mod) {
    setForm(function(prev) {
      var list = prev.modality || [];
      if (list.includes(mod)) {
        return Object.assign({}, prev, { modality: list.filter(function(m) { return m !== mod; }) });
      }
      return Object.assign({}, prev, { modality: list.concat([mod]) });
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-50 flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-purple-400" />
            Sucursales
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {branches.length}/{maxBranches} sucursales utilizadas
          </p>
        </div>
        {canAddMore && !showForm && (
          <Button onClick={openNewForm} className="bg-purple-500 hover:bg-purple-600" data-testid="add-branch-btn">
            <Plus className="w-4 h-4 mr-2" />Agregar Sucursal
          </Button>
        )}
        {!canAddMore && (
          <span className="text-amber-400 text-sm">Límite de {maxBranches} sucursales alcanzado</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-500"
          style={{ width: (branches.length / maxBranches * 100) + '%' }}
        />
      </div>

      {/* Form */}
      {showForm && (
        <Card className="bg-slate-900 border-purple-500/30" data-testid="branch-form">
          <CardHeader className="pb-4">
            <CardTitle className="text-slate-50 flex items-center justify-between">
              <span>{editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}</span>
              <Button variant="ghost" size="sm" onClick={function() { setShowForm(false); setEditingBranch(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nombre de la Sucursal *</Label>
                  <Input value={form.name} onChange={function(e) { handleChange('name', e.target.value); }} placeholder="Ej: Nexovital - Sucursal Viña del Mar" className="bg-slate-800 border-slate-700 text-slate-50" data-testid="branch-name" required />
                </div>
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input type="email" value={form.email} onChange={function(e) { handleChange('email', e.target.value); }} placeholder="contacto@sucursal.cl" className="bg-slate-800 border-slate-700 text-slate-50" data-testid="branch-email" required />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Textarea value={form.description} onChange={function(e) { handleChange('description', e.target.value); }} placeholder="Describe esta sucursal..." rows={3} className="bg-slate-800 border-slate-700 text-slate-50 resize-none" />
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-slate-300">Dirección *</Label>
                  <Input value={form.address} onChange={function(e) { handleChange('address', e.target.value); }} placeholder="Av. Libertad 123" className="bg-slate-800 border-slate-700 text-slate-50" data-testid="branch-address" required />
                </div>
                <div>
                  <Label className="text-slate-300">Ciudad *</Label>
                  <Input value={form.city} onChange={function(e) { handleChange('city', e.target.value); }} placeholder="Viña del Mar" className="bg-slate-800 border-slate-700 text-slate-50" data-testid="branch-city" required />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Región</Label>
                  <Select value={form.region} onValueChange={function(v) { handleChange('region', v); }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {REGIONS.map(function(r) { return <SelectItem key={r} value={r} className="text-slate-300">{r}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Teléfono *</Label>
                  <Input value={form.phone} onChange={function(e) { handleChange('phone', e.target.value); }} placeholder="+56912345678" className="bg-slate-800 border-slate-700 text-slate-50" data-testid="branch-phone" required />
                </div>
                <div>
                  <Label className="text-slate-300">WhatsApp</Label>
                  <Input value={form.whatsapp} onChange={function(e) { handleChange('whatsapp', e.target.value); }} placeholder="+56912345678" className="bg-slate-800 border-slate-700 text-slate-50" />
                </div>
              </div>

              {/* Modality */}
              <div>
                <Label className="text-slate-300 mb-2 block">Modalidad</Label>
                <div className="flex flex-wrap gap-2">
                  {['Residencial', 'Ambulatorio', 'Online', 'Mixto'].map(function(mod) {
                    var isActive = (form.modality || []).includes(mod.toLowerCase());
                    return (
                      <button key={mod} type="button" onClick={function() { toggleModality(mod.toLowerCase()); }}
                        className={'px-3 py-1.5 rounded-full text-sm border transition-colors ' + (isActive ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                      >{mod}</button>
                    );
                  })}
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Precio mínimo (CLP)</Label>
                  <Input type="number" value={form.price_min} onChange={function(e) { handleChange('price_min', e.target.value ? parseInt(e.target.value) : ''); }} placeholder="500000" className="bg-slate-800 border-slate-700 text-slate-50" />
                </div>
                <div>
                  <Label className="text-slate-300">Precio máximo (CLP)</Label>
                  <Input type="number" value={form.price_max} onChange={function(e) { handleChange('price_max', e.target.value ? parseInt(e.target.value) : ''); }} placeholder="2000000" className="bg-slate-800 border-slate-700 text-slate-50" />
                </div>
                <div>
                  <Label className="text-slate-300">Género</Label>
                  <Select value={form.gender} onValueChange={function(v) { handleChange('gender', v); }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="mixto" className="text-slate-300">Mixto</SelectItem>
                      <SelectItem value="hombre" className="text-slate-300">Solo Hombres</SelectItem>
                      <SelectItem value="mujer" className="text-slate-300">Solo Mujeres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Website */}
              <div>
                <Label className="text-slate-300">Sitio Web</Label>
                <Input value={form.website} onChange={function(e) { handleChange('website', e.target.value); }} placeholder="https://www.ejemplo.cl" className="bg-slate-800 border-slate-700 text-slate-50" />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="bg-purple-500 hover:bg-purple-600 flex-1" data-testid="branch-submit">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingBranch ? 'Guardar Cambios' : 'Crear Sucursal'}
                </Button>
                <Button type="button" variant="outline" onClick={function() { setShowForm(false); setEditingBranch(null); }} className="border-slate-700 text-slate-400">
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Branches List */}
      {branches.length === 0 && !showForm && (
        <Card className="bg-slate-900 border-slate-800 border-dashed">
          <CardContent className="py-12 text-center">
            <GitBranch className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <h3 className="text-slate-400 font-medium mb-2">Sin sucursales</h3>
            <p className="text-slate-500 text-sm mb-6">Tu Plan Elite te permite agregar hasta {maxBranches} sucursales con badge Elite incluido.</p>
            <Button onClick={openNewForm} className="bg-purple-500 hover:bg-purple-600">
              <Plus className="w-4 h-4 mr-2" />Agregar Primera Sucursal
            </Button>
          </CardContent>
        </Card>
      )}

      {branches.map(function(branch) {
        var isExpanded = expandedBranch === branch.center_id;
        return (
          <Card key={branch.center_id} className="bg-slate-900 border-slate-800 hover:border-purple-500/30 transition-colors" data-testid={'branch-card-' + branch.branch_number}>
            <CardContent className="p-0">
              {/* Branch Header */}
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={function() { setExpandedBranch(isExpanded ? null : branch.center_id); }}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-400 font-bold text-sm">#{branch.branch_number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-slate-50 font-semibold truncate">{branch.name}</h3>
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full font-medium flex-shrink-0">Elite</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{branch.city}</span>
                      {branch.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{branch.phone}</span>}
                      {branch.email && <span className="flex items-center gap-1 hidden sm:flex"><Mail className="w-3 h-3" />{branch.email}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={function(e) { e.stopPropagation(); openEditForm(branch); }} className="text-slate-400 hover:text-emerald-400" data-testid={'edit-branch-' + branch.branch_number}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={function(e) { e.stopPropagation(); handleDelete(branch.center_id, branch.name); }} className="text-slate-400 hover:text-red-400" data-testid={'delete-branch-' + branch.branch_number}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-slate-800 p-4 space-y-3">
                  {branch.description && (
                    <p className="text-slate-400 text-sm">{branch.description}</p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500 block text-xs">Dirección</span>
                      <span className="text-slate-300">{branch.address}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Región</span>
                      <span className="text-slate-300">{branch.region || '-'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Modalidad</span>
                      <span className="text-slate-300">{(branch.modality || []).join(', ') || '-'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Género</span>
                      <span className="text-slate-300">{branch.gender || 'Mixto'}</span>
                    </div>
                  </div>
                  {(branch.price_min || branch.price_max) && (
                    <div className="text-sm">
                      <span className="text-slate-500 text-xs">Precio: </span>
                      <span className="text-emerald-400">
                        {branch.price_min ? '$' + branch.price_min.toLocaleString('es-CL') : ''} 
                        {branch.price_min && branch.price_max ? ' - ' : ''}
                        {branch.price_max ? '$' + branch.price_max.toLocaleString('es-CL') : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>ID: {branch.center_id}</span>
                    <span>|</span>
                    <span>Creada: {branch.created_at ? new Date(branch.created_at).toLocaleDateString('es-CL') : '-'}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
